import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import type { DashboardLaneTone } from "@/v2-backoffice-client/uptime/dashboard/uptime.dashboard.types"

export type UptimeDashboardLaneProps = ComponentProps<"section"> & {
  // Lane title rendered in the lane header. Operators read this
  // first so it stays plain ("Down", "Slow", "Configuration drift").
  title: string
  // Icon next to the title. Lined up with the lane tone but kept
  // independent so the same icon can be reused for non-lane
  // contexts (e.g. drift section in the regular page).
  icon: "triangle-warning" | "signal-alt-1" | "swap"
  // Tone drives border + header background colour.
  tone: DashboardLaneTone
  // Copy shown when the lane is empty. Important: a lane that says
  // "empty" must explain *why* (e.g. "No services are down" rather
  // than just "—").
  emptyText: string
  // Total count rendered in the lane header. We accept it as a
  // separate prop instead of deriving from children because the
  // children may already be a sliced subset (e.g. virtualised) and
  // the count should always reflect the whole set.
  totalCount: number
  children: ReactNode
}

// Lane shell on the wall-display dashboard. A lane is a vertical
// column with a coloured header (tone), a count badge, and a
// scrollable body that holds the actual rows. The body shows every
// matching row so operators can scan or scroll through the full
// list -- there's no "...and N more" cap; the body just scrolls.
function UptimeDashboardLane(props: UptimeDashboardLaneProps) {
  const {
    title,
    icon,
    tone,
    emptyText,
    totalCount,
    children,
    className,
    ...sectionProps
  } = props

  return (
    <section
      className={cn(
        "uptime-dashboard-lane",
        `uptime-dashboard-lane-${tone}`,
        totalCount === 0 && "uptime-dashboard-lane-empty",
        className,
      )}
      {...sectionProps}
    >
      <header className="uptime-dashboard-lane-header">
        <Icon src={icon} />
        <span className="uptime-dashboard-lane-title">{title}</span>
        <span className="uptime-dashboard-lane-count">{totalCount}</span>
      </header>
      <div className="uptime-dashboard-lane-body">
        {totalCount === 0 ? (
          <div className="uptime-dashboard-lane-empty-text">{emptyText}</div>
        ) : (
          children
        )}
      </div>
    </section>
  )
}

export default UptimeDashboardLane
